import type { BonusMeteo } from '../api/types'
import {
  ATTRIBUTION_METEO,
  duree,
  heureBamako,
  LIEN_ATTRIBUTION_METEO,
  libelleSymbole,
  releveCentre,
  resumer,
} from '../api/meteo'
import { libelleMeteo, type ConditionMeteo } from '../api/tarifs'
import { fcfa } from './Format'
import { Icone } from './Icone'
import { PilotageMeteo } from './meteo/PilotageMeteo'
import { useMeteo } from './meteo/useMeteo'

/**
 * Barre météo de Bamako.
 *
 * Ce n'est pas un ornement : une averse de saison des pluies change le barème.
 * Le bonus livreur et le supplément client suivent `platform_config.meteo_actuelle`,
 * et la barre est l'endroit où le dispatch voit quel barème s'applique quand il
 * affecte une course.
 *
 * Depuis 0061, la météo s'active TOUTE SEULE : l'automate serveur relève 5 points
 * de Bamako toutes les 10 min (MET Norway) et décide. La barre affiche ce qu'il a
 * relevé et décidé — plus aucun appel météo depuis le navigateur (l'ancien appel
 * Open-Meteo, usage non commercial seulement, est supprimé) — et donne au dispatch
 * ses deux leviers : Arrêter, Forcer. Un bandeau annonce chaque décision de
 * l'automate en temps réel.
 */
export function BarreMeteo({
  baremes,
  meteoActuelle = null,
  surBascule,
}: {
  baremes: BonusMeteo[]
  /** Météo en vigueur (0043) ; null = base pas à jour. */
  meteoActuelle?: ConditionMeteo | null
  /** La condition a changé (automate ou dispatch) : relire la configuration. */
  surBascule?: () => Promise<void> | void
}) {
  const { donnees, absente, annonce, effacerAnnonce, recharger } = useMeteo(() => void surBascule?.())

  const bareme = meteoActuelle ? baremes.find((b) => b.condition === meteoActuelle) : undefined
  const bonus = bareme?.bonusLivreur ?? 0
  const client = bareme?.supplementClient ?? 0
  const centre = donnees ? releveCentre(donnees) : null
  const ciel = libelleSymbole(centre?.symbole ?? null)
  const resume = donnees && meteoActuelle ? resumer(meteoActuelle, donnees) : null
  const icone = meteoActuelle === 'rain' ? 'rain' : meteoActuelle === 'extreme_heat' ? 'sun' : ciel.icone

  return (
    <div className={`weather-bar${meteoActuelle && meteoActuelle !== 'normal' ? ' wx-active' : ''}`} style={{ flexWrap: 'wrap' }}>
      <span className="wx-ic">
        <Icone nom={icone} />
      </span>
      <strong>Météo Bamako :</strong>
      <span>
        {centre ? (
          <>
            {ciel.libelle}
            {centre.temperatureC != null && ` — ${Math.round(centre.temperatureC)} °C`}
          </>
        ) : absente ? (
          'relevés automatiques à venir (migration 0061)'
        ) : donnees ? (
          'aucun relevé exploitable'
        ) : (
          'chargement…'
        )}
      </span>
      {meteoActuelle && (
        <span className={`badge ${meteoActuelle === 'normal' ? 'badge-gray' : 'badge-orange'}`}>
          Tarif en vigueur : {libelleMeteo(meteoActuelle)}
          {resume?.depuis && meteoActuelle !== 'normal' ? ` depuis ${heureBamako(resume.depuis)} (${duree(resume.depuis, null)})` : ''}
        </span>
      )}
      {bonus || client ? (
        <>
          <span className="badge badge-yellow">Bonus livreur +{fcfa(bonus)}</span>
          {Boolean(client) && <span className="badge badge-blue">Suppl. client +{fcfa(client)}</span>}
        </>
      ) : (
        <span className="badge badge-gray">Aucun bonus</span>
      )}
      {resume && <span className={`wx-source wx-source-${resume.source}`}>{resume.texteSource}</span>}
      {donnees && (
        <span className={`wx-status${donnees.etat.sourceIndisponible ? '' : ' live'}`}>
          relevé {heureBamako(donnees.etat.dernierReleve)} · prochaine vérification {heureBamako(donnees.etat.prochaineVerification)} (heure Bamako)
        </span>
      )}
      {resume && donnees?.reglages.mode === 'auto' && <PilotageMeteo resume={resume} surFait={async () => { await recharger(); await surBascule?.() }} compact />}
      {resume?.avertissement && <div className="wx-propose wx-alerte">{resume.avertissement}</div>}
      {annonce && (
        <div className="wx-propose wx-annonce" role="status">
          <span>{annonce}</span>
          <button className="btn btn-outline btn-sm" onClick={effacerAnnonce}>
            Compris
          </button>
        </div>
      )}
      {donnees && (
        <a className="wx-attribution" href={LIEN_ATTRIBUTION_METEO} target="_blank" rel="noreferrer">
          {ATTRIBUTION_METEO}
        </a>
      )}
    </div>
  )
}
